import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { ServiceRequestService } from '@/services/serviceRequestService';
import { JobProposal, ProposalFeedbackRequest } from '@/types/serviceRequest';
import { MessageCircle, ThumbsUp, ThumbsDown, Star } from 'lucide-react';

interface ProposalFeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
  proposal: JobProposal;
  onSuccess: (conversationId?: string) => void;
}

export const ProposalFeedbackModal: React.FC<ProposalFeedbackModalProps> = ({
  isOpen,
  onClose,
  proposal,
  onSuccess
}) => {
  const [feedbackType, setFeedbackType] = useState<'USER_INTERESTED' | 'USER_REJECTED' | null>(null);
  const [userFeedback, setUserFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async () => {
    if (!feedbackType) {
      toast({
        title: "Error",
        description: "Please select your response to the proposal.",
        variant: "destructive"
      });
      return;
    }

    if (feedbackType === 'USER_REJECTED' && !userFeedback.trim()) {
      toast({
        title: "Error", 
        description: "Please provide feedback when rejecting a proposal.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const request: ProposalFeedbackRequest = {
        jobProposalId: proposal.id,
        status: feedbackType,
        userFeedback: userFeedback.trim() || undefined
      };

      const response = await ServiceRequestService.submitProposalFeedback(request);

      if (response.success) {
        toast({
          title: "Success",
          description: response.message || "Your feedback has been submitted successfully.",
          variant: "default"
        });
        
        onSuccess(response.conversationId);
        onClose();
      } else {
        toast({
          title: "Error",
          description: response.error || "Failed to submit feedback. Please try again.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error('Error submitting proposal feedback:', error);
      toast({
        title: "Error",
        description: "Failed to submit feedback. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setFeedbackType(null);
      setUserFeedback('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-5 h-5" />
            Respond to Proposal
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Business and Proposal Summary */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-3 mb-2">
              {proposal.business?.company_logo && (
                <img
                  src={proposal.business.company_logo}
                  alt={proposal.business.name}
                  className="w-8 h-8 rounded-full object-cover"
                />
              )}
              <div>
                <h4 className="font-medium text-gray-900">
                  {proposal.business?.name || 'Service Provider'}
                </h4>
                {proposal.business?.reviews && proposal.business.reviews.length > 0 && (
                  <div className="flex items-center gap-1">
                    <Star className="w-3 h-3 text-yellow-400 fill-current" />
                    <span className="text-xs text-gray-600">
                      {(proposal.business.reviews.reduce((sum: number, review: { rating: number }) => sum + review.rating, 0) / proposal.business.reviews.length).toFixed(1)} 
                      ({proposal.business.reviews.length} reviews)
                    </span>
                  </div>
                )}
              </div>
            </div>
            
            {proposal.quote && (
              <div className="text-sm text-gray-600">
                <strong>Quote:</strong> £{proposal.quote}
              </div>
            )}
            
            {proposal.response_message && (
              <div className="text-sm text-gray-600 mt-2">
                <strong>Message:</strong> {proposal.response_message}
              </div>
            )}
          </div>

          {/* Feedback Options */}
          <div className="space-y-3">
            <h5 className="font-medium text-gray-900">Your Response:</h5>
            
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant={feedbackType === 'USER_INTERESTED' ? 'default' : 'outline'}
                onClick={() => setFeedbackType('USER_INTERESTED')}
                className={`h-auto p-4 flex flex-col items-center gap-2 ${
                  feedbackType === 'USER_INTERESTED' 
                    ? 'bg-green-600 hover:bg-green-700' 
                    : 'border-green-200 hover:border-green-300'
                }`}
                disabled={isSubmitting}
              >
                <ThumbsUp className={`w-5 h-5 ${
                  feedbackType === 'USER_INTERESTED' ? 'text-white' : 'text-green-600'
                }`} />
                <span className={`text-sm font-medium ${
                  feedbackType === 'USER_INTERESTED' ? 'text-white' : 'text-green-600'
                }`}>
                  I'm Interested
                </span>
                <span className={`text-xs text-center ${
                  feedbackType === 'USER_INTERESTED' ? 'text-green-100' : 'text-gray-500'
                }`}>
                  Start a conversation
                </span>
              </Button>

              <Button
                variant={feedbackType === 'USER_REJECTED' ? 'default' : 'outline'}
                onClick={() => setFeedbackType('USER_REJECTED')}
                className={`h-auto p-4 flex flex-col items-center gap-2 ${
                  feedbackType === 'USER_REJECTED' 
                    ? 'bg-red-600 hover:bg-red-700' 
                    : 'border-red-200 hover:border-red-300'
                }`}
                disabled={isSubmitting}
              >
                <ThumbsDown className={`w-5 h-5 ${
                  feedbackType === 'USER_REJECTED' ? 'text-white' : 'text-red-600'
                }`} />
                <span className={`text-sm font-medium ${
                  feedbackType === 'USER_REJECTED' ? 'text-white' : 'text-red-600'
                }`}>
                  Not Interested
                </span>
                <span className={`text-xs text-center ${
                  feedbackType === 'USER_REJECTED' ? 'text-red-100' : 'text-gray-500'
                }`}>
                  Decline with feedback
                </span>
              </Button>
            </div>
          </div>

          {/* Feedback Text Area */}
          {feedbackType && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                {feedbackType === 'USER_INTERESTED' 
                  ? 'Optional message (will be shared with the business):'
                  : 'Why are you not interested? (required):'
                }
              </label>
              <Textarea
                value={userFeedback}
                onChange={(e) => setUserFeedback(e.target.value)}
                placeholder={
                  feedbackType === 'USER_INTERESTED'
                    ? "I'd like to discuss the details further..."
                    : "Please let them know why this proposal isn't suitable..."
                }
                disabled={isSubmitting}
                className="min-h-[80px]"
              />
              {feedbackType === 'USER_REJECTED' && (
                <p className="text-xs text-gray-500">
                  Your feedback helps businesses improve their proposals.
                </p>
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!feedbackType || isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Submitting...
                </>
              ) : (
                feedbackType === 'USER_INTERESTED' ? 'Start Conversation' : 'Submit Feedback'
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};